/** @jsxImportSource @emotion/react */
import React, { FocusEvent, useCallback, useEffect, useState } from 'react'
import { Form as FinalForm, FormRenderProps } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import { findLast, sortBy } from 'lodash'
import { generate as generateUniqueId } from 'randomstring'
import Typography from '@mui/material/Typography'
import { OnChange } from 'react-final-form-listeners'
import { useParams } from 'react-router-dom'
import slugify from 'slugify'
import { Button } from '@mui/material'
import { ApolloError } from '@apollo/client'
import { FieldValidator, FormApi, SubmissionErrors } from 'final-form'
import { PRACTICE_TEST_MEDIA_PATH_S3 } from 'constants/images'
import { PlatformSlug } from 'constants/consts'
import { InputField } from 'components/Input'
import QuestionPicker from 'components/QuestionPicker'
import { Revision, Revisions } from 'components/Revisions'
import DetailsBlock from 'components/DetailsBlock'
import QuestionAccordion from 'components/QuestionAccordion'
import { EditorField, prepareEditorHtml } from 'components/Editor'
import { MembershipLevelSelect } from 'components/MembershipLevelSelect'
import TabsNavigation from 'components/TabsNavigation'
import { CourseName } from 'theme/utils'
import { validateRequired, validateRequiredDuration, validateRequiredHtml } from 'utils/validation'
import {
  CreatePracticeTestInput,
  DentalSchoolsEnum,
  MembershipType,
  PassageSubCategoryEnum,
  PassageTypeEnum,
  PracticeTestTypeEnum,
  QuestionInvertTypeEnum,
  QuestionLocationTypeEnum,
  QuestionTypeEnum,
} from '__generatedTypes__/globalTypes'
import { findOnePracticeTest_findOnePracticeTest as PT } from '__generatedTypes__/findOnePracticeTest'
import { findAllQuestion_findAllQuestion_items as Question } from '__generatedTypes__/findAllQuestion'
import { TabsItem } from 'types/tabsItem'
import { Tooltip } from 'components/Tooltip'
import { SelectField } from 'components/Select'
import { SlugInputField } from 'components/SlugInputField/SlugInputField'
import { useQuestionsSubmitError } from 'hooks/useQuestionsSubmitError'
import { toast } from 'utils/toast'
import * as commonClasses from 'pagesNew/Video/commonStyles'
import { GREPassagesFieldArray } from 'pagesNew/QuestionBanks/Form/GREPassagesFieldArray'
import { validatePassages as _validateGrePassages } from 'pagesNew/QuestionBanks/Form/validatePassages'
import { getAvailableQuestionTypes } from 'pagesNew/QuestionBanks/utils'
import { NOT_SELECTED_SCHOOL, School } from 'pagesNew/Users/Edit/components/hooks'
import { renderSchoolItem } from 'pagesNew/Users/Edit/components/FacultyFields'
import { WEB_APP_ORIGIN } from 'pagesNew/Users'
import { BreakSectionFormItem, validateBreakSections } from '../utils'
import { PT_MIN_SCORES } from '../consts'
import * as classes from './styles'
import { BreakSectionsFieldArray } from './BreakSectionsFieldArray'
import { TestResultsFieldArray } from './TestResultsFieldArray'
import {
  getInitialPassages,
  isCarsPT,
  isPassagesAvailable,
  Passage,
  PASSAGE_CATEGORIES,
  PassagesFieldArray,
} from './PassagesFieldArray'
import { SCORE_ITEMS } from './TestResultsFieldArray/consts'
import { Results } from './Results'
import { GRE_PRACTICE_TEST_QUESTION_COUNT, GRE_SECTIONS, GRE_SECTIONS_HINT, getGreSectionBadge } from './greSections'

export type PracticeTest = PT & {
  questions: Question[]
}

export type Props = {
  type: PracticeTestTypeEnum
  title: string
  practiceTestOrSubjectId: PracticeTest | string
  practiceTestCategoryId?: string
  subject: PracticeTest['subject']
  onSubmit: (questionBank: CreatePracticeTestInput) => SubmissionErrors | Promise<SubmissionErrors> | void
  onDelete?: () => Promise<void>
  getDeleteDisabledReason?: (isLive: boolean) => string | undefined
  passFailStandardizedScore?: boolean
  isInPersonTest?: boolean
  schools?: School[]
  defaultOrder?: number
  tagLabel?: string
  isUsedForAdditionalTests?: boolean
  questionLocationType?: QuestionLocationTypeEnum
}

export type TestResult = Omit<PracticeTest['testResult'][0], 'DATScore'> & {
  DATScore: number | { id: string; title: string }
}

export type FormState = Omit<PracticeTest, 'duration' | 'passage' | 'testResult' | 'university' | 'breaksSections'> & {
  duration?: string
  passage?: Passage[]
  breakSections: BreakSectionFormItem[]
  testResult: TestResult[]
  university?: School | null
}

type TabsItemType = TabsItem & {
  fields: Array<keyof FormState>
}

const COVER_PAGE_TAB: TabsItemType = {
  id: 'cover-page',
  title: 'Cover Page',
  fields: ['title', 'membershipLevel', 'duration', 'pageDescriptionAsHtml', 'introAsHtml'],
}

const BUILDER_TAB: TabsItemType = {
  id: 'builder',
  title: 'Builder',
  fields: ['questions'],
}

const BREAKS_SECTIONS_TAB: TabsItemType = {
  id: 'break-sections',
  title: 'Break Sections',
  fields: ['breakSections'],
}

export const TEST_RESULTS_TAB: TabsItemType = {
  id: 'test-results',
  title: 'Test Results',
  fields: ['testResult'],
}

const PASSAGES_TAB: TabsItemType = {
  id: 'passage',
  title: 'Passages',
  fields: ['passage'],
}

const GRE_PASSAGES_TAB: TabsItemType = {
  id: 'gre-passages',
  title: 'Passages',
  fields: ['passage'],
}

const RESULTS_TAB: TabsItemType = {
  id: 'results',
  title: 'Results',
  fields: [],
}

const TABS: TabsItemType[] = [
  COVER_PAGE_TAB,
  BUILDER_TAB,
  BREAKS_SECTIONS_TAB,
  PASSAGES_TAB,
  GRE_PASSAGES_TAB,
  TEST_RESULTS_TAB,
  RESULTS_TAB,
]

const isHasError = (
  fields: Array<keyof FormState>,
  errors: FormRenderProps['errors'],
  touched: FormRenderProps['touched']
) => fields.some((field) => touched?.[field] && errors?.[field])

export const durationToNumber = (durationString: string) => {
  const [hours = 0, minutes = 0] = durationString.split(':')
  return +hours * 60 + +minutes
}

export const durationToString = (durationNumber: number) => {
  const hours = Math.floor(durationNumber / 60)
  const minutes = Math.floor(durationNumber % 60)
  return `${hours}:${String(minutes).padStart(2, '0')}`
}

const focusOnErrors = createDecorator<FormState, Partial<FormState>>()

const BAD_SLUG_ERROR_MESSAGE = 'Practice Test with this slug already created :('

export const handleServerErrors = (error: unknown, title: string) => {
  if (error && typeof error === 'object' && 'graphQLErrors' in error) {
    const existingSlugErrorIndex = (error.graphQLErrors as ApolloError['graphQLErrors']).findIndex(({ message }) =>
      [BAD_SLUG_ERROR_MESSAGE].includes(message)
    )

    if (existingSlugErrorIndex !== -1) {
      return { slug: `${title} with this slug already exists` }
    }
  }
}

const shouldKeepPassageId = (passage: Passage, initialValues: Partial<FormState>) => {
  if (initialValues.type !== PracticeTestTypeEnum.MultiSectionGREPracticeTest) {
    return !!passage.id
  }

  const initialPassage = initialValues.passage?.find((item) => item.id && item.id === passage.id)

  if (!initialPassage?.id) {
    return false
  }

  const contentChanged = (passage.content ?? '') !== (initialPassage.content ?? '')
  const rangeChanged =
    Number(passage.startQuestion) !== Number(initialPassage.startQuestion) ||
    Number(passage.endQuestion) !== Number(initialPassage.endQuestion)

  return !contentChanged && !rangeChanged
}

export const prepareForSubmit = async (
  values: Partial<FormState>,
  initialValues: Partial<FormState>,
  isInPersonTest?: boolean
): Promise<CreatePracticeTestInput> => {
  const [introAsHtml, pageDescriptionAsHtml, formattedPassages, formattedTestResults] = await Promise.all([
    prepareEditorHtml(values.introAsHtml ?? '', PRACTICE_TEST_MEDIA_PATH_S3),
    prepareEditorHtml(values.pageDescriptionAsHtml ?? '', PRACTICE_TEST_MEDIA_PATH_S3),
    Promise.all(
      values.passage?.map(async (passage) => {
        const content = await prepareEditorHtml(passage.content ?? '', PRACTICE_TEST_MEDIA_PATH_S3)

        return {
          ...(shouldKeepPassageId(passage, initialValues) ? { id: passage.id } : {}),
          title: passage.title ?? '',
          startQuestion: Number(passage.startQuestion),
          endQuestion: Number(passage.endQuestion),
          content,
          category: passage.category?.id ?? null,
          subCategory: passage.subCategory?.id ?? null,
          reference: passage.reference ?? null,
          watchPassageWalkthrough: passage.watchPassageWalkthrough ?? null,
          invertContentImages: passage.invertContentImages ?? QuestionInvertTypeEnum.AUTO,
        }
      }) ?? []
    ),
    Promise.all(
      values.testResult?.map(async (testResult) => {
        const resultTextAsHtml = await prepareEditorHtml(testResult.resultTextAsHtml ?? '', PRACTICE_TEST_MEDIA_PATH_S3)

        return {
          id: testResult.id,
          DATScore:
            typeof testResult.DATScore === 'object' ? +(testResult.DATScore?.id ?? 0) : +(testResult.DATScore ?? 0),
          resultPercantage: +(testResult.resultPercantage ?? 100),
          resultTextAsHtml,
        }
      }) ?? []
    ),
  ])

  const university =
    !values.university?.id || values.university?.id === NOT_SELECTED_SCHOOL.id
      ? null
      : (values.university?.id as DentalSchoolsEnum)

  const breaksSections = sortBy(
    values.breakSections?.map(({ id, questionIndex, duration }) => ({
      id: id ?? generateUniqueId(),
      questionIndex: Number(questionIndex) - 1,
      duration: Number(duration),
    })) ?? [],
    'questionIndex'
  )

  return {
    type: values.type,
    duration: durationToNumber(values.duration || '0:00'),
    history: values.history,
    isLive: isInPersonTest ? !!university : values.isLive,
    order: values.order,
    introAsHtml,
    pageDescriptionAsHtml,
    subject: values.subject?.id ? { id: values.subject.id } : undefined,
    practiceTestCategory: values.practiceTestCategory?.id ? { id: values.practiceTestCategory.id } : undefined,
    membershipLevel: values.membershipLevel ?? MembershipType.Free,
    questionIds: values.questions?.map(({ id }) => id),
    questions: values.questions?.map(({ id }) => ({ id })),
    passage: formattedPassages,
    breaksSections,
    testResult: formattedTestResults,
    title: values.title,
    university,
    slug: values.slug || null,
  }
}

const getDATScore = (score: number) => findLast(SCORE_ITEMS, ({ id }) => score >= +id) ?? SCORE_ITEMS[0]

export const getTestResults = (
  type: PracticeTestTypeEnum,
  practiceTestOrSubjectId: PracticeTest | string,
  platformSlug: string,
  passFailStandardizedScore: boolean,
  isInPersonTest?: boolean
): TestResult[] => {
  if (type === PracticeTestTypeEnum.MultiSectionGREPracticeTest) {
    return []
  }

  if (typeof practiceTestOrSubjectId === 'string') {
    if (isInPersonTest) {
      return [
        {
          resultPercantage: 0,
          DATScore: SCORE_ITEMS[0],
          resultTextAsHtml: SCORE_ITEMS[0].title,
        } as TestResult,
        {
          resultPercantage: 60,
          DATScore: SCORE_ITEMS[1],
          resultTextAsHtml: SCORE_ITEMS[1].title,
        } as TestResult,
        {
          resultPercantage: 70,
          DATScore: SCORE_ITEMS[2],
          resultTextAsHtml: SCORE_ITEMS[2].title,
        } as TestResult,
        {
          resultPercantage: 79,
          DATScore: SCORE_ITEMS[3],
          resultTextAsHtml: SCORE_ITEMS[3].title,
        } as TestResult,
      ]
    }

    return [
      {
        resultPercantage: 0,
        DATScore: passFailStandardizedScore ? SCORE_ITEMS[0] : PT_MIN_SCORES[platformSlug],
      } as TestResult,
    ]
  }

  if (practiceTestOrSubjectId?.testResult?.length) {
    const collectionToSort = passFailStandardizedScore
      ? practiceTestOrSubjectId.testResult.map((testResult) => ({
          ...testResult,
          DATScore: getDATScore(testResult.DATScore),
        }))
      : practiceTestOrSubjectId.testResult

    return sortBy<TestResult>(collectionToSort, 'resultPercantage')
  }

  return [
    {
      resultPercantage: 0,
    } as TestResult,
  ]
}

const getBreakSections = (practiceTestOrSubjectId: PracticeTest | string): BreakSectionFormItem[] => {
  if (typeof practiceTestOrSubjectId === 'string') {
    return []
  }

  return sortBy(
    practiceTestOrSubjectId.breaksSections?.map(({ id, questionIndex, duration }) => ({
      id,
      questionIndex: questionIndex + 1,
      duration,
    })) ?? [],
    'questionIndex'
  )
}

const getInitialValues = ({
  type,
  practiceTestOrSubjectId,
  practiceTestCategoryId,
  platformSlug,
  passFailStandardizedScore,
  defaultOrder = 0,
  isInPersonTest = false,
  schools = [],
}: {
  type: PracticeTestTypeEnum
  practiceTestOrSubjectId: PracticeTest | string
  platformSlug: string
  passFailStandardizedScore: boolean
  practiceTestCategoryId?: string
  defaultOrder?: number
  isInPersonTest?: boolean
  schools?: School[]
}): Partial<FormState> => {
  const testResult = getTestResults(
    type,
    practiceTestOrSubjectId,
    platformSlug,
    passFailStandardizedScore,
    isInPersonTest
  )

  if (typeof practiceTestOrSubjectId === 'string') {
    return {
      type,
      membershipLevel:
        type === PracticeTestTypeEnum.ReadingQuestionBank || isCarsPT(type) || isInPersonTest
          ? MembershipType.Free
          : undefined,
      history: [],
      isLive: false,
      order: defaultOrder,
      subject: { id: practiceTestOrSubjectId } as PracticeTest['subject'],
      practiceTestCategory: practiceTestCategoryId
        ? ({ id: practiceTestCategoryId } as PracticeTest['practiceTestCategory'])
        : undefined,
      questionIds: [],
      questions: [],
      testResult,
      breakSections: [],
      passage: getInitialPassages(type),
      title: '',
      university: isInPersonTest ? NOT_SELECTED_SCHOOL : null,
      slug: '',
    }
  }

  return {
    ...practiceTestOrSubjectId,
    type: practiceTestOrSubjectId.type ?? type,
    duration: durationToString(practiceTestOrSubjectId.duration ?? 0),
    questions: (practiceTestOrSubjectId.questionIds
      .map((questionId) => practiceTestOrSubjectId.questions.find(({ id }) => id === questionId))
      .filter((question) => !!question) ?? []) as PracticeTest['questions'],
    passage: practiceTestOrSubjectId.passage?.length
      ? sortBy(practiceTestOrSubjectId.passage, 'startQuestion').map((passage) => {
          const category = PASSAGE_CATEGORIES[passage.category as PassageTypeEnum] ?? null
          const subCategory =
            category?.subCategories?.find(
              (subCategory) => subCategory.id === (passage.subCategory as PassageSubCategoryEnum)
            ) ?? null

          return {
            ...passage,
            category,
            subCategory,
            invertContentImages: passage.invertContentImages ?? QuestionInvertTypeEnum.AUTO,
          }
        })
      : [],
    testResult,
    breakSections: getBreakSections(practiceTestOrSubjectId),
    university: practiceTestOrSubjectId.university
      ? schools.find((school) => school.id === practiceTestOrSubjectId.university)
      : NOT_SELECTED_SCHOOL,
    slug: practiceTestOrSubjectId.slug ?? '',
  }
}

const validateQuestions: FieldValidator<Question[]> = (questions: Question[] | undefined, allValues: object) => {
  const values = allValues as FormState

  if (values.type === PracticeTestTypeEnum.MultiSectionGREPracticeTest) {
    if (questions?.length !== GRE_PRACTICE_TEST_QUESTION_COUNT) {
      return `Must include ${GRE_PRACTICE_TEST_QUESTION_COUNT} questions`
    }
  }

  return validateRequired(questions)
}

const validatePassages: FieldValidator<Passage[]> = (value: Passage[]) => {
  const baseError = validateRequired(value)

  if (baseError) {
    return baseError
  }

  const errors = value.reduce((errors, passage, index) => {
    const prevPassageEnd = value[index - 1]?.endQuestion

    if (+(passage.startQuestion ?? 0) > +(passage.endQuestion ?? 0)) {
      return {
        ...errors,
        [index]: {
          startQuestion: 'Should not be more then End Question',
        },
      }
    }

    if (+(passage.startQuestion ?? 0) <= +(prevPassageEnd ?? 0)) {
      return {
        ...errors,
        [index]: {
          startQuestion: `Should be no common questions for Passages #${index} and #${index + 1}`,
        },
      }
    }

    if (+(passage.startQuestion ?? 0) > +(prevPassageEnd ?? 0) + 1) {
      return {
        ...errors,
        [index]: {
          startQuestion: `Should be no gap between questions for Passages #${index} and #${index + 1}`,
        },
      }
    }

    return errors
  }, {} as Record<string, Record<string | number, string> | string>)

  if (Object.keys(errors).length) {
    return errors
  }
}

const validateGrePassages: FieldValidator<Passage[]> = (value: Passage[], allValues: object) =>
  _validateGrePassages(value, (allValues as FormState).questions, GRE_SECTIONS)

export const Form = ({
  type,
  title,
  practiceTestOrSubjectId,
  practiceTestCategoryId,
  subject,
  onSubmit,
  onDelete,
  getDeleteDisabledReason,
  passFailStandardizedScore = false,
  isInPersonTest = false,
  schools = [],
  tagLabel,
  defaultOrder = 0,
  isUsedForAdditionalTests,
  questionLocationType = QuestionLocationTypeEnum.PT,
}: Props) => {
  const { platform } = useParams()
  const platformSlug = platform as string
  const isGrePracticeTest = type === PracticeTestTypeEnum.MultiSectionGREPracticeTest

  const [activeTab, setActiveTab] = useState<TabsItem>(COVER_PAGE_TAB)
  const [submitting, setSubmitting] = useState(false)
  const { submitQuestionError, setSubmitQuestionError, handleServerError } = useQuestionsSubmitError()
  const [initialValues, setInitialValues] = useState(
    getInitialValues({
      type,
      practiceTestOrSubjectId,
      practiceTestCategoryId,
      defaultOrder,
      platformSlug,
      passFailStandardizedScore,
      isInPersonTest,
      schools,
    })
  )
  const isEditMode = !!initialValues.id
  const [isSlugEdited, setIsSlugEdited] = useState(isEditMode)

  useEffect(() => {
    setInitialValues(
      getInitialValues({
        type,
        practiceTestOrSubjectId,
        practiceTestCategoryId,
        defaultOrder,
        platformSlug,
        passFailStandardizedScore,
        isInPersonTest,
        schools,
      })
    )
    // TODO: need to figure out why without "?.id" useEffect runs and resets initialValues
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
  }, [practiceTestOrSubjectId?.id, platformSlug])

  const handleSubmit = useCallback(
    async (values: FormState) => {
      try {
        const practiceTestData = await prepareForSubmit(values, initialValues, isInPersonTest)
        const errors = await onSubmit(practiceTestData)

        if (errors) {
          return errors
        }

        setSubmitQuestionError(null)
      } catch (error) {
        toast.error('Failed to prepare data for submit')
        handleServerError(error)
      }
    },
    [onSubmit, isInPersonTest, initialValues]
  )

  const handleDelete = useCallback(async () => {
    setSubmitting(true)
    try {
      await onDelete?.()
    } catch (e) {
      console.error(e)
    }
    setSubmitting(false)
  }, [onDelete])

  const handleTitleChange =
    (onChange: (name: keyof FormState, value: string) => void) => (e: FocusEvent<HTMLInputElement>) => {
      if (!isInPersonTest || isSlugEdited) {
        return
      }

      onChange('slug', slugify(e.target.value, { lower: true }))
    }

  const handleSlugBlur =
    (onChange: (name: keyof FormState, value: string) => void) => (e: FocusEvent<HTMLInputElement>) => {
      onChange('slug', slugify(e.target.value, { lower: true }))
    }

  const handleCopyTestLink = (form: FormApi<FormState>) => () => {
    if (!isInPersonTest) {
      return
    }

    const university = form.getFieldState('university')?.value?.id
    const slug = form.getFieldState('slug')?.value

    if (!university || !slug || university === NOT_SELECTED_SCHOOL.id) {
      toast.error(`Failed to copy ${title} URL! University/slug is incorrect.`)
      return
    }

    const link = `${WEB_APP_ORIGIN}/${subject.platform.slug}/classroom/in-person-mock/${university}/${slug}`
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.success(`${title} URL copied to clipboard!`)
      })
      .catch(() => {
        toast.error(`Failed to copy ${title} URL!`)
      })
  }

  return (
    <div css={commonClasses.container}>
      <Typography variant="title3" css={commonClasses.title}>
        {isEditMode ? 'Edit' : 'Create'} {title}
      </Typography>
      <FinalForm<FormState>
        mutators={{ ...arrayMutators }}
        decorators={[focusOnErrors]}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, initialValues, submitting: formSubmitting, form, values, errors, touched }) => {
          const slugInputProps = {
            name: 'slug',
            validate: validateRequired,
            label: 'Slug',
            placeholder: 'Enter Slug',
            fullWidth: true,
            onBlur: handleSlugBlur(form.change),
            onChange: () => setIsSlugEdited(true),
            startAdornment: (
              <span
                css={classes.slugAdornment(!values.university?.id || values.university?.id === NOT_SELECTED_SCHOOL.id)}
              >
                /{values.university?.id}/
              </span>
            ),
            endAdornment: (
              <Button onClick={handleCopyTestLink(form)} size="small" css={classes.copyButton}>
                Copy URL
              </Button>
            ),
          }

          const hideMembershipSelect = [
            PracticeTestTypeEnum.ReadingQuestionBank,
            PracticeTestTypeEnum.DailyCarsPractice,
          ].includes(type)

          const isMiniTest = type === PracticeTestTypeEnum.MiniTest || type === PracticeTestTypeEnum.ReadingMiniTest
          const isMockTest = type === PracticeTestTypeEnum.MockTest
          const questionsCount = values.questions?.length ?? 0

          return (
            <form onSubmit={handleSubmit} css={commonClasses.contentContainer}>
              {isPassagesAvailable(type) && (
                <OnChange name="questions">
                  {(questions) => {
                    const passagesValue = form.getFieldState('passage')?.value ?? []
                    form.change(
                      'passage',
                      passagesValue.map((passage, index) => {
                        if (index === passagesValue.length - 1) {
                          return {
                            ...passage,
                            endQuestion: questions.length,
                          }
                        }

                        return passage
                      })
                    )
                  }}
                </OnChange>
              )}

              <div css={commonClasses.block}>
                <TabsNavigation
                  tabs={TABS.filter((tab) => {
                    if (tab.id === PASSAGES_TAB.id) {
                      return isPassagesAvailable(type)
                    }

                    if (tab.id === GRE_PASSAGES_TAB.id) {
                      return isGrePracticeTest
                    }

                    if (tab.id === BREAKS_SECTIONS_TAB.id) {
                      return isMockTest
                    }

                    if (tab.id === TEST_RESULTS_TAB.id) {
                      return !isInPersonTest && !isGrePracticeTest
                    }

                    if (tab.id === RESULTS_TAB.id) {
                      return isInPersonTest && isEditMode
                    }

                    return true
                  }).map((tab) => ({
                    ...tab,
                    error: isHasError(tab.fields, errors, touched),
                  }))}
                  value={activeTab}
                  onChange={setActiveTab}
                />

                <div css={classes.tabContent(activeTab.id === COVER_PAGE_TAB.id)}>
                  <InputField
                    name="title"
                    validate={validateRequired}
                    label="Title"
                    placeholder="Enter Title"
                    onBlur={handleTitleChange(form.change)}
                    fullWidth
                  />
                  {isInPersonTest ? (
                    <SelectField
                      name="university"
                      validate={validateRequired}
                      label="University"
                      items={[NOT_SELECTED_SCHOOL, ...(schools ?? [])]}
                      renderItem={renderSchoolItem}
                      placeholder="Select University"
                      fullWidth
                      disabled={!schools?.length}
                    />
                  ) : (
                    !hideMembershipSelect &&
                    (isUsedForAdditionalTests ? (
                      <Tooltip
                        title={`You can't change Membership Level for this ${title} because it is used for Additional Tests product`}
                        isNote
                        arrow
                      >
                        <MembershipLevelSelect name="membershipLevel" validate={validateRequired} fullWidth disabled />
                      </Tooltip>
                    ) : (
                      <MembershipLevelSelect name="membershipLevel" validate={validateRequired} fullWidth />
                    ))
                  )}
                  {!isMiniTest && !isGrePracticeTest && (
                    <InputField
                      name="duration"
                      validate={validateRequiredDuration}
                      label="Duration of Test"
                      placeholder="Enter Duration of Test (HH:MM)"
                      fullWidth
                    />
                  )}
                  {!isInPersonTest && !isMiniTest && !isGrePracticeTest && (
                    <EditorField
                      type="default"
                      name="pageDescriptionAsHtml"
                      label="Page Description"
                      validate={validateRequiredHtml}
                      placeholder="Enter Page Description"
                    />
                  )}
                  {!isMiniTest && !isGrePracticeTest && (
                    <EditorField
                      type="default"
                      name="introAsHtml"
                      label="Introduction Description"
                      validate={validateRequiredHtml}
                      placeholder="Enter Introduction Description"
                    />
                  )}
                  {isInPersonTest &&
                    (isEditMode ? <SlugInputField {...slugInputProps} /> : <InputField {...slugInputProps} />)}
                </div>

                <div css={classes.tabContent(activeTab.id === BUILDER_TAB.id)}>
                  <FieldArray<Question> name="questions" validate={validateQuestions}>
                    {({ fields, meta }) => (
                      <>
                        <QuestionPicker
                          label="Questions"
                          value={fields.value ?? []}
                          onChange={(questions) => form.change('questions', questions)}
                          error={meta.touched && meta.error}
                          formHelperText={meta.touched && typeof meta.error === 'string' ? meta.error : undefined}
                          filter={{
                            platform: { id: subject.platform.id },
                            types: isCarsPT(type)
                              ? [QuestionTypeEnum.SingleChoiceCARS]
                              : getAvailableQuestionTypes({ platformSlug: platformSlug as PlatformSlug }),
                          }}
                          willUsedIn={questionLocationType}
                          hideSelectedItems
                          fullWidth
                        />
                        {isGrePracticeTest && (
                          <Typography variant="body2" css={classes.greSectionsHint}>
                            {GRE_SECTIONS_HINT}
                          </Typography>
                        )}
                        <QuestionAccordion
                          questions={fields.value ?? []}
                          onSort={(questions) => form.change('questions', questions)}
                          onDeleteClick={(question, index) => fields.remove(index)}
                          onQuestionUpdated={(questionUpd, index) => fields.update(index, questionUpd)}
                          passages={isGrePracticeTest ? values?.passage ?? [] : undefined}
                          getSectionBadge={isGrePracticeTest ? getGreSectionBadge : undefined}
                        />
                      </>
                    )}
                  </FieldArray>
                </div>

                {isPassagesAvailable(type) && (
                  <div css={classes.tabContent(activeTab.id === PASSAGES_TAB.id)}>
                    <FieldArray name="passage" validate={validatePassages}>
                      {({ fields, meta }) => <PassagesFieldArray type={type} fields={fields} meta={meta} />}
                    </FieldArray>
                  </div>
                )}

                {isGrePracticeTest && (
                  <div css={classes.tabContent(activeTab.id === GRE_PASSAGES_TAB.id)}>
                    <FieldArray name="passage" validate={validateGrePassages}>
                      {({ fields, meta }) => <GREPassagesFieldArray fields={fields} meta={meta} />}
                    </FieldArray>
                  </div>
                )}

                {isInPersonTest && isEditMode && (
                  <div css={classes.tabContent(activeTab.id === RESULTS_TAB.id)}>
                    <Results platformId={subject.platform.id} type={type} practiceTestId={initialValues.id ?? ''} />
                  </div>
                )}

                <div css={classes.tabContent(activeTab.id === BREAKS_SECTIONS_TAB.id)}>
                  <FieldArray name="breakSections" validate={validateBreakSections}>
                    {({ fields, meta }) => (
                      <BreakSectionsFieldArray fields={fields} meta={meta} questionsCount={questionsCount} />
                    )}
                  </FieldArray>
                </div>

                {!isGrePracticeTest && (
                  <div css={classes.tabContent(activeTab.id === TEST_RESULTS_TAB.id)}>
                    <FieldArray name="testResult" validate={validateRequired}>
                      {({ fields, meta }) => (
                        <TestResultsFieldArray
                          fields={fields}
                          meta={meta}
                          passFailStandardizedScore={passFailStandardizedScore}
                        />
                      )}
                    </FieldArray>
                  </div>
                )}

                {!!initialValues.history?.length && (
                  <Revisions revisions={(initialValues?.history ?? []) as Revision[]} />
                )}
              </div>
              <DetailsBlock
                title="Test Details"
                itemName={title}
                tag={subject.tabName as CourseName}
                tagLabel={tagLabel}
                createDate={initialValues?.createdAt}
                statusLive={!!initialValues?.isLive}
                onDeleteClick={handleDelete}
                disabled={formSubmitting || submitting}
                deleteDisabledReason={
                  getDeleteDisabledReason?.(!!initialValues.isLive) ||
                  (isUsedForAdditionalTests
                    ? `You can't delete ${title} because it is used for Additional Tests product`
                    : initialValues.isLive
                    ? `You can't delete ${title} because Live is enabled`
                    : undefined)
                }
                errorText={submitQuestionError}
              />
            </form>
          )
        }}
      />
    </div>
  )
}
